/**
 * Copy and paste between the user's computer and the workshop's screen (PLAN-ATELIER-OTTO § 17.6):
 * which keyboard shortcut does what, as pure rules.
 *
 * noVNC sends every key it sees to the machine and cancels the browser's own action, so Cmd+V never
 * pastes and Cmd+C copies nothing. On a Mac it also turns Cmd into Alt for the machine (Linux), where
 * Cmd+C would arrive as Alt+C. The viewer therefore looks at the shortcut first, before noVNC:
 *  - paste: the browser's own paste is let through into a hidden field (no clipboard permission
 *    asked), its text handed to the machine, then Ctrl+V typed there;
 *  - copy / cut: Ctrl+C / Ctrl+X typed on the machine, whose answer comes back as text;
 *  - other editing shortcuts (select all, undo, redo, find) become their Ctrl version;
 *  - any other Cmd shortcut stays the computer's own (reload, new tab…), never sent as Alt.
 */

export type ShortcutAction =
  | { kind: 'paste' }
  /** Ctrl+C / Ctrl+X to type on the machine; `native`: noVNC already types it (not a Mac). */
  | { kind: 'copy'; letter: 'c' | 'x'; native: boolean }
  /** An editing shortcut to type on the machine as Ctrl+letter (Shift kept). */
  | { kind: 'translate'; letter: string; shift: boolean }
  /** Not for the machine: the computer keeps it, noVNC never sees it. */
  | { kind: 'local' };

const TRANSLATED = new Set(['a', 'z', 'y', 'f']);

export interface KeyLike {
  code: string;
  /** Used only when `code` is empty (some virtual keyboards and synthetic events leave it out). */
  key?: string;
  metaKey: boolean;
  ctrlKey: boolean;
  altKey: boolean;
  shiftKey: boolean;
}

/** The letter of a `KeyA`…`KeyZ` code (else of a one-letter `key`), in lower case; null otherwise. Pure. */
function letterOf(code: string, key = ''): string | null {
  const match = /^Key([A-Z])$/.exec(code);
  if (match) return match[1].toLowerCase();
  return code === '' && /^[a-z]$/i.test(key) ? key.toLowerCase() : null;
}

/** What a key press means for copy and paste, or null when noVNC handles it as usual. Pure. */
export function shortcutAction(key: KeyLike, isMac: boolean): ShortcutAction | null {
  if (isMac && (key.code === 'MetaLeft' || key.code === 'MetaRight' || (key.code === '' && key.key === 'Meta'))) {
    return { kind: 'local' };
  }
  const shortcut = isMac ? key.metaKey : key.ctrlKey && !key.metaKey;
  if (!shortcut || key.altKey) return isMac && key.metaKey ? { kind: 'local' } : null;
  const letter = letterOf(key.code, key.key);
  if (letter === 'v' && !key.shiftKey) return { kind: 'paste' };
  if ((letter === 'c' || letter === 'x') && !key.shiftKey) return { kind: 'copy', letter, native: !isMac };
  if (!isMac) return null;
  if (letter && TRANSLATED.has(letter)) return { kind: 'translate', letter, shift: key.shiftKey };
  return { kind: 'local' };
}

/** X11 keysyms the viewer types (noVNC's `sendKey`). */
export const KEYSYM = { controlL: 0xffe3, shiftL: 0xffe1 } as const;

/** The keysym of a letter: lower case alone, upper case with Shift. Pure. */
export function letterKeysym(letter: string, shift: boolean): number {
  return (shift ? letter.toUpperCase() : letter.toLowerCase()).charCodeAt(0);
}

/** How long the machine's answer to Ctrl+C is waited for, and how fresh a selection may stand in. */
export const COPY_WAIT_MS = 800;
export const SELECTION_FRESH_MS = 120_000;

/**
 * The text a copy on the machine puts on the computer: the machine's answer after Ctrl+C, else — the
 * machine sends a selection once, and may not send it again when Ctrl+C copies the same text — the
 * last text it sent, if recent. Null when there is nothing to copy. Pure.
 */
export function copiedText(input: {
  requestedAt: number;
  now: number;
  last: { text: string; at: number } | null;
}): string | null {
  const last = input.last;
  if (!last || last.text === '') return null;
  if (last.at >= input.requestedAt) return last.text;
  return input.now - last.at <= SELECTION_FRESH_MS ? last.text : null;
}

/**
 * The machine's clipboard as it was meant. VNC carries the clipboard as Latin-1 and noVNC reads it so;
 * but the machine's Chrome hands its text over as UTF-8 even when asked for Latin-1 (`STRING`), so
 * « é » arrived as « Ã© » (Gilles, 08/10/2026 : « ça copie pas bien les accents »). Text that reads as
 * valid multi-byte UTF-8 is decoded as such; anything else (a real Latin-1 « é » alone is not valid
 * UTF-8) is left as it came. Pure.
 */
export function fromMachine(text: string): string {
  const bytes = new Uint8Array(text.length);
  for (let i = 0; i < text.length; i += 1) {
    const code = text.charCodeAt(i);
    if (code > 0xff) return text;
    bytes[i] = code;
  }
  if (!bytes.some((b) => b >= 0x80)) return text;
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    return text;
  }
}

/** Typographic characters VNC cannot carry (outside Latin-1), and their closest Latin-1 writing. */
const LATIN1_STAND_INS: Record<string, string> = {
  '‘': "'", '’': "'", '‚': "'", '′': "'",
  '“': '"', '”': '"', '„': '"', '″': '"',
  '–': '-', '—': '-', '−': '-', '‐': '-', '‑': '-',
  '…': '...', '•': '-', '€': 'EUR',
  'œ': 'oe', 'Œ': 'OE', 'Ÿ': 'Y',
  ' ': ' ', ' ': ' ', '​': '',
};

/**
 * Text to hand to the machine. VNC carries Latin-1 only, and noVNC turns anything else into « ? »:
 * French accents pass, but the typographic apostrophe, « œ », « € » or a long dash would not. They are
 * written with their closest Latin-1 equivalent instead; what has none still becomes « ? ». Pure.
 */
export function toMachine(text: string): string {
  return Array.from(text.normalize('NFC'), (ch) => LATIN1_STAND_INS[ch] ?? ch).join('');
}
